<template>
  <h2 class="header" id="projects">{{ $t('cv.projects') }}</h2>
  <section class="container">
    <div class="filter">
      <div
        class="category"
        v-for="(category, index) in projects"
        :key="category.title"
        @click="activecategory = index"
        :class="{ active: activecategory === index }"
      >
        <span class="iconcategory">
          <ic :icon="category.icon" />
        </span>
        <span class="titlecategory" v-html="category.title" />
      </div>
    </div>
    <div class="categorydesc" v-html="projects[activecategory].description" />
    <article
      v-for="proj in projects[activecategory].projects"
      :key="proj.title"
    >
      <div class="header">
        <div class="iconcont">
          <img :src="proj.icon" alt="Project Image" />
          <div class="name" v-html="proj.title" @click="open(proj.github)" />
        </div>
        <div class="langs">
          <div v-for="lang in proj.langs" :key="lang" class="lang">
            <div class="dot" :style="{ backgroundColor: colors[lang] }" />
            <div v-html="lang" />
          </div>
        </div>
      </div>
      <div class="description">{{ proj.description }}</div>
      <div class="links">
        <!-- <a
          v-for="link in proj.links"
          :key="link.href"
          :href="link.href"
          target="_blank"
          v-html="link.text"
        /> -->
      </div>
    </article>
  </section>
  <Languages />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Languages from '@/components/Details/Languages.vue'

// 1. Importujemy projekty
import projectsPl from '@/data/pl/projects'
import projectsEn from '@/data/en/projects'
import projectsDe from '@/data/de/projects'
import projectsEs from '@/data/es/projects'
import projectsRu from '@/data/ru/projects'
import projectsJp from '@/data/jp/projects'

// 2. Importujemy kolory (zwróć uwagę na aliasy as, jeśli importujesz konkretną stałą)
import { colors as colorsPl } from '@/data/pl/colors'
import { colors as colorsEn } from '@/data/en/colors'
import { colors as colorsDe } from '@/data/de/colors'
import { colors as colorsEs } from '@/data/es/colors'
import { colors as colorsRu } from '@/data/ru/colors'
import { colors as colorsJp } from '@/data/jp/colors'

// Interfejsy (skopiowane z naszego pierwszego rozwiązania dla projektów)
interface Project {
  title: string
  description: string
  github: string
  icon: string
  langs: string[]
  main?: boolean
  simulation?: boolean
  screenshots?: string[]
  tags?: string[]
}

interface ProjectGroup {
  title: string
  icon: string
  description: string
  projects: Project[]
}

// Mapy języków
const projectsMap: Record<string, ProjectGroup[]> = {
  pl: projectsPl,
  en: projectsEn,
  de: projectsDe,
  es: projectsEs,
  ru: projectsRu,
  jp: projectsJp,
}

// Zakładam, że kolory to prosty obiekt typu { "Vue": "#42b883", "React": "#61dafb" }
const colorsMap: Record<string, Record<string, string>> = {
  pl: colorsPl,
  en: colorsEn,
  de: colorsDe,
  es: colorsEs,
  ru: colorsRu,
  jp: colorsJp,
}

export default defineComponent({
  components: {
    Languages,
  },
  // W data() zostawiamy tylko to, co może się dynamicznie zmieniać w trakcie używania komponentu
  data() {
    return {
      activecategory: 0,
    }
  },
  computed: {
    // Dynamiczne dane na podstawie języka
    projects(): ProjectGroup[] {
      const locale = this.$i18n.locale as string
      return projectsMap[locale] || projectsMap['en']
    },
    colors(): Record<string, string> {
      const locale = this.$i18n.locale as string
      return colorsMap[locale] || colorsMap['en']
    },
  },
  methods: {
    open(a: string) {
      window.open(a, '_blank')
    },
  },
})
</script>

<style lang="scss" scoped>
.container {
  padding: 10px;
  margin: 0 auto;
  text-align: justify;
  font-size: 15px;
}
article {
  margin: 5px auto;
  padding: 5px 0;
  width: 100%;
  max-width: 900px;
  border-bottom: 1px solid $light;
}

.header {
  display: flex;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 6px;
  }

  .name {
    font-weight: 500;
    font-size: 19px;
    color: $dark;
    cursor: pointer;

    @media (max-width: 768px) {
      font-size: 17px;
    }
  }
  .langs {
    font-size: 15px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;

    .lang {
      display: flex;
      align-items: center;

      .dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        display: inline-block;
        background-color: $dark_gray;
        margin-right: 5px;
      }
    }
  }
}

.iconcont {
  display: flex;
  align-items: center;
}

img {
  width: 40px;
  height: 40px;
  margin-right: 10px;
  vertical-align: middle;
  border-radius: 8px;
}

.description {
  margin: 10px 0;
  font-size: 14px;
}

.filter {
  display: flex;
  margin: auto;
  margin-bottom: 20px;
  color: $light;
  max-width: 1050px;
  border-radius: 5px;
  overflow: hidden;
  user-select: none;
  flex-wrap: wrap;

  .category {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    cursor: pointer;
    padding: 10px;
    transition: 0.2s all;
    background: $dark;
    flex-grow: 1;
    text-align: center;

    &:hover {
      background-color: $dark_gray;
    }

    &.active {
      background-color: $green;
    }

    @media (max-width: 768px) {
      padding: 8px 6px;
      font-size: 13px;
    }
  }

  .iconcategory {
    font-size: 20px;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  .titlecategory {
    margin-left: 5px;

    @media (max-width: 768px) {
      display: none;
    }
  }
}

.categorydesc {
  margin: 5px auto;
  width: 100%;
  max-width: 900px;
  font-size: 15px;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid $light;
  text-align: center;
}
</style>
